import { Injectable } from '@angular/core';
import {SupportedCountry, VatFormat} from "../model/vat-types.interface";

@Injectable({
  providedIn: 'root'
})
export class VatFormatterService {

  private readonly vatFormats: { [key: string]: VatFormat } = {
    'CH': {
      prefix: 'CHE-',
      maxDigits: 9,
      formatter: (digits: string) => {
        if (digits.length <= 3) {
          return `CHE-${digits}`;
        } else if (digits.length <= 6) {
          return `CHE-${digits.slice(0, 3)}.${digits.slice(3)}`;
        } else {
          return `CHE-${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}`;
        }
      },
      validator: (formatted: string) => /^CHE-\d{3}\.\d{3}\.\d{3}$/.test(formatted),
      placeholder: 'CHE-xxx.xxx.xxx',
      example: 'CHE-114.367.720'
    },
    'DE': {
      prefix: 'DE',
      maxDigits: 9,
      formatter: (digits: string) => `DE${digits}`,
      validator: (formatted: string) => /^DE\d{9}$/.test(formatted),
      placeholder: 'DExxxxxxxxx',
      example: 'DE300619864'
    }
  };

  /**
   * Format VAT number as user types
   */
  formatAsUserTypes(input: string, countryCode: string): string {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    if (!format) return input;

    const digits = this.extractDigits(input);

    const limitedDigits = digits.substring(0, format.maxDigits);

    return format.formatter(limitedDigits);
  }

  /**
   * Format complete VAT number (for paste events or complete input)
   */
  formatComplete(input: string, countryCode: string): string {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    if (!format) return input;

    const digits = this.extractDigits(input);

    if (digits.length === format.maxDigits) {
      return format.formatter(digits);
    }

    return this.formatAsUserTypes(input, countryCode);
  }

  /**
   * Handle country change with existing VAT data
   */
  handleCountryChange(currentVat: string, newCountryCode: string): string {
    if (!currentVat || !currentVat.trim()) {
      return this.getSupportedCountries().some(c => c.code === newCountryCode?.toUpperCase())
        ? this.getInitialValue(newCountryCode)
        : '';
    }

    const digits = this.extractDigits(currentVat);
    if (!digits) {
      return this.getSupportedCountries().some(c => c.code === newCountryCode?.toUpperCase())
        ? this.getInitialValue(newCountryCode)
        : '';
    }

    const newFormat = this.vatFormats[newCountryCode?.toUpperCase()];
    if (!newFormat) {
      return digits;
    }

    if (digits.length === newFormat.maxDigits) {
      return newFormat.formatter(digits);
    } else if (digits.length < newFormat.maxDigits) {
      return newFormat.formatter(digits);
    } else {
      const truncatedDigits = digits.substring(0, newFormat.maxDigits);
      return newFormat.formatter(truncatedDigits);
    }
  }

  /**
   * Get initial value when country is selected
   */
  getInitialValue(countryCode: string): string {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    return format ? format.prefix : '';
  }

  /**
   * Validate formatted VAT number
   */
  isValid(formatted: string, countryCode: string): boolean {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    if (!format) return false;

    return format.validator(formatted);
  }

  /**
   * Check if VAT has correct number of digits
   */
  hasCorrectDigitCount(input: string, countryCode: string): boolean {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    if (!format) return false;

    const digits = this.extractDigits(input);
    return digits.length === format.maxDigits;
  }

  /**
   * Get placeholder text
   */
  getPlaceholder(countryCode: string): string {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    return format ? format.placeholder : '';
  }

  /**
   * Get example format
   */
  getExample(countryCode: string): string {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    return format ? format.example : '';
  }

  /**
   * Get max length for input field
   */
  getMaxLength(countryCode: string): number {
    const format = this.vatFormats[countryCode?.toUpperCase()];
    if (!format) return 20;

    return format.example.length;
  }

  /**
   * Extract only digits from string
   */
  private extractDigits(input: string): string {
    return input.replace(/\D/g, '');
  }

  /**
   * Extract digits for unsupported country conversion
   */
  extractDigitsForUnsupportedCountry(currentVat: string): string {
    return this.extractDigits(currentVat);
  }

  /**
   * Check if VAT belongs to a specific country format
   */
  detectCountryFromVat(vatNumber: string): string | null {
    if (!vatNumber) return null;

    for (const [countryCode, format] of Object.entries(this.vatFormats)) {
      if (format.validator(vatNumber)) {
        return countryCode;
      }
    }

    if (vatNumber.startsWith('CHE-') || vatNumber.startsWith('CHE')) {
      return 'CH';
    }
    if (vatNumber.startsWith('DE')) {
      return 'DE';
    }

    return null;
  }

  /**
   * Check if current VAT matches selected country
   */
  isVatCountryMismatch(vatNumber: string, selectedCountry: string): boolean {
    if (!vatNumber || !selectedCountry) return false;

    const detectedCountry = this.detectCountryFromVat(vatNumber);
    return detectedCountry !== null && detectedCountry !== selectedCountry.toUpperCase();
  }

  /**
   * Get supported countries
   */
  getSupportedCountries(): SupportedCountry[] {
    return [
      { code: 'CH', name: 'Switzerland', example: this.getExample('CH') },
      { code: 'DE', name: 'Germany', example: this.getExample('DE') }
    ];
  }

  /**
   * Check if user input should trigger formatting
   */
  shouldFormat(input: string, countryCode: string): boolean {
    if (!countryCode || !input) return false;

    const format = this.vatFormats[countryCode.toUpperCase()];
    if (!format) return false;

    const digits = this.extractDigits(input);
    return digits.length > 0;
  }
}
